import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { format } from 'date-fns';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import { adminAiCacheService, AiWorkoutMetMiss, MetMissReason } from '@services/admin-ai-cache.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';

export const MET_MISS_REASON_LABELS: Record<MetMissReason, string> = {
  no_candidates: 'Кандидатів не знайдено (0)',
  rejected: 'Кандидати були, AI жоден не прийняв',
  invalid_code: 'AI повернув код не з кандидатів',
  no_duration: 'MET вибрано, але невідомий час одиниці',
};

const REASON_HINTS: Record<MetMissReason, string> = {
  no_candidates: 'Пошук по базі MET нічого не знайшов — додати ключові фрази до відповідної MET-активності.',
  rejected: 'Кандидати не підійшли — перевірити ключові фрази або чи є така активність у Compendium.',
  invalid_code: 'AI повернув код поза списком кандидатів — збій відповіді AI.',
  no_duration: 'Незрозуміло, скільки триває одиниця (км, м) — перевірити одиницю запису.',
};

const INTENSITY_LABELS: Record<string, string> = { low: 'низька', medium: 'середня', high: 'висока' };

const formatDate = (iso: string) => format(new Date(iso), 'dd.MM.yyyy HH:mm');

/** "Not found in MET" journal: activities the AI estimated without the MET base, most frequent first. */
export const WorkoutMetMissesTab: React.FC = () => {
  const [items, setItems] = useState<AiWorkoutMetMiss[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState<MetMissReason | ''>('');
  const [confirmClear, setConfirmClear] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAiCacheService.getWorkoutMetMisses({
        page, limit, search: debouncedSearch || undefined, reason: reason || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити журнал «не знайдено в MET».');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, reason]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleClear = async () => {
    setConfirmClear(false);
    try {
      const deleted = await adminAiCacheService.clearWorkoutMetMisses();
      toastService.success(`Журнал «не знайдено в MET» очищено: ${deleted} записів`);
      setPage(1);
      fetchItems();
    } catch {
      toastService.error('Не вдалося очистити журнал.');
    }
  };

  return (
    <PageCard>
      <PageToolbar
        storageKey="filters:admin-ai-workout-met-misses"
        filters={[
          {
            id: 'search',
            type: 'text',
            label: 'Пошук за назвою',
            value: search,
            onChange: (v) => { setSearch(v); setPage(1); },
            flexGrow: 1,
          },
          {
            id: 'reason',
            type: 'select',
            label: 'Причина',
            value: reason,
            onChange: (v) => { setReason(v as MetMissReason | ''); setPage(1); },
            options: [
              { value: '', label: 'Всі' },
              ...(Object.keys(MET_MISS_REASON_LABELS) as MetMissReason[]).map((r) => ({ value: r, label: MET_MISS_REASON_LABELS[r] })),
            ],
          },
        ]}
        onReset={() => { setSearch(''); setReason(''); setPage(1); }}
        showReset
        extraActions={
          <Button variant="outlined" color="warning" startIcon={<DeleteSweepOutlinedIcon />} onClick={() => setConfirmClear(true)}>
            Очистити журнал
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Записів не знайдено
        </Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Одиниця</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell align="center">Разів</TableCell>
                <TableCell>Кандидати MET</TableCell>
                <TableCell>Схожість</TableCell>
                <TableCell>Код від AI</TableCell>
                <TableCell>Вперше</TableCell>
                <TableCell>Востаннє</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.nameKey}</Typography>
                  </TableCell>
                  <TableCell>
                    {item.unit || '—'}{INTENSITY_LABELS[item.intensity] ? ` · ${INTENSITY_LABELS[item.intensity]}` : ''}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={REASON_HINTS[item.reason] ?? ''}>
                      <Chip label={MET_MISS_REASON_LABELS[item.reason] ?? item.reason} size="small" variant="outlined" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.occurrences} size="small" color={item.occurrences > 1 ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: 'break-word' }}>
                    <Typography variant="caption">{item.candidateCodes?.split(',').join(', ') || '—'}</Typography>
                  </TableCell>
                  <TableCell>{item.topScore != null ? Number(item.topScore).toFixed(3) : '—'}</TableCell>
                  <TableCell>{item.aiMetCode ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.firstSeenAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.lastSeenAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey="pagination:admin-ai-workout-met-misses"
      />

      <ConfirmationDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={handleClear}
        title="Очистити журнал «не знайдено в MET»?"
        message="Буде видалено журнал активностей, для яких в офіційній базі MET не знайшлося відповідника. Кеш розрахунків лишається. Якщо потрібні поточні дані - спершу зробіть «Експорт (XLSX)» на вкладці кешу (аркуш «Not found in MET»). Дію не можна скасувати."
        confirmText="Очистити"
        cancelText="Скасувати"
      />
    </PageCard>
  );
};
